import { IPPVCountry, IPPVCountryConfig, IPPVEvents, IPPVPaymentMethod, IUserFullObject } from '@intigral/types';
import { isPaymentMethodSelected } from './payment';
import { REWARD_QUEST_TYPE } from '@intigral/constant/common';

/**
 * Function to determine whether the user should select global or user country config.
 * @param {IUserFullObject} user User object
 * @returns {string} Return as 'global' or 'user'
 */
export const whichCountryConfig = (user: IUserFullObject, inAppUser?: boolean): 'global' | 'user' => {
    if (inAppUser) {
        return 'global';
    }
    else if (isPaymentMethodSelected(user)) {
        return 'user';
    }
    return 'global';
};

/**
 * Retrieves a specific country for a given country.
 *
 * @param {IPPVCountryConfig} ppvCountryConfig - The PPV country configuration object.
 * @param {string} countryCode - country code.
 * @returns {IPPVPaymentMethod | null} The matched country if found, otherwise null.
 */
export const getPPVSelectedCountry = (ppvCountryConfig: IPPVCountryConfig, countryCode: string): IPPVCountry | null => {
    if (ppvCountryConfig?.countries?.length > 0) {
        return (
            ppvCountryConfig.countries?.find(
                (country) => country.countryCode?.toLowerCase() === countryCode?.toLowerCase()
            ) ?? null
        );
    }
    return null;
};

/**
 * Retrieves a specific payment method for a given country and payment type from the PPV country configuration.
 *
 * @param {IPPVCountryConfig} ppvCountryConfig - The PPV country configuration object.
 * @param {string} countryCode - country code.
 * @param {string} paymentType - The payment type.
 * @returns {IPPVPaymentMethod | null} The matched payment method if found, otherwise null.
 */
export const getPPVPaymentMethods = (
    ppvCountryConfig: IPPVCountryConfig,
    countryCode: string,
    paymentType: string
): IPPVPaymentMethod | null => {
    if (ppvCountryConfig?.countries?.length > 0) {
        const selectedCountry = getPPVSelectedCountry(ppvCountryConfig, countryCode);
        return (
            selectedCountry?.paymentMethods?.find(
                (paymentMethod) => paymentMethod.paymentType?.toLowerCase() === paymentType?.toLowerCase()
            ) ?? null
        );
    }
    return null;
};

/**
 * Retrieves the selected PPV event based on country code, payment type, and event ID.
 *
 * @param {IPPVCountryConfig} ppvCountryConfig - The full PPV country configuration object.
 * @param {string} countryCode - country code.
 * @param {string} paymentType - The type of payment method.
 * @param {string} eventIds - The ID of the event to find.
 * @returns {IPPVEvent | null} The matching event object if found, otherwise null.
 */
export const getPPVSelectedEvent = (
    ppvCountryConfig: IPPVCountryConfig,
    countryCode: string,
    paymentType: string,
    eventIds: string[]
): IPPVEvents[] => {
    if (ppvCountryConfig?.countries?.length > 0) {
        const selectedPayment = getPPVPaymentMethods(ppvCountryConfig, countryCode, paymentType);
        return (
            selectedPayment?.events?.filter((event) => {
                const isExists = eventIds.find((eventId) => eventId?.toLowerCase() === event.eventId?.toLowerCase());
                if (isExists) {
                    return true;
                }
                return false;
            }) ?? []
        );
    }
    return [];
};

export const isSubscriptionRenewQuest = (questTypeId: string) => {
    return questTypeId?.toLowerCase() === REWARD_QUEST_TYPE.SUBSCRIPTION_RENEWAL.toLowerCase() ? true : false;
};
export const isSubscriptionUpgradeQuest = (questTypeId: string) => {
    return questTypeId?.toLowerCase() === REWARD_QUEST_TYPE.TIER_UPGRADE.toLowerCase() ? true : false;
};

export const parseDatadogAllowedTracingUrls = (value: string | undefined, excludedUrls: string | undefined) => {
    const excludedPatterns = (excludedUrls || '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);

    const allowedTracingUrls = (value || '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((pattern) => ({
            match: (url: string) => url.includes(pattern) && !excludedPatterns.some((excludedPattern) => url.includes(excludedPattern)),
            propagatorTypes: ['datadog' as const],
        }));

    return allowedTracingUrls;
}